import React, { useEffect, useMemo, useState } from 'react';
import chroma from 'chroma-js';
import { withWindow } from '@shared/utils/withWindow';
import { throttle } from 'lodash-es';
import { StyledStickyContainer, StyledContentWrapper } from './StickyNavBar.styles';
import { useStickyNavBarOffsetData } from '../StickyNavBarOffset';
import { pxToRem } from '@withjoy/joykit/theme';
import type { NavBarProps } from '../../layouts/LayoutBrannan/components/NavBar';
import { NavBar } from '../../layouts/LayoutBrannan/components/NavBar';
import type { NavbarEventPropsInterface } from '../../layouts/layout.types';

interface StickyNavBarProps
  extends Readonly<Omit<NavBarProps, 'variant'>>,
    Readonly<{
      eventProps: NavbarEventPropsInterface;
      pageColor?: string;
      scrollableContainerRef: React.RefObject<HTMLDivElement>;
    }> {}

export const StickyNavBar: React.FC<StickyNavBarProps> = ({ pageColor, scrollableContainerRef, ...navBarProps }) => {
  const [isScrolledDown, setIsScrolledDown] = useState(false);
  const { offset, isVisible: isVisibleFromContext } = useStickyNavBarOffsetData();

  useEffect(() => {
    const handleScroll = throttle(() => {
      const scrollTop = scrollableContainerRef.current?.scrollTop || 0;
      withWindow(global => setIsScrolledDown(scrollTop > global.innerHeight));
    }, 100);

    const container = scrollableContainerRef.current;

    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }

    return () => undefined;
  }, [scrollableContainerRef]);

  const backgroundColor = useMemo(() => {
    return chroma(pageColor || '#ffffff')
      .alpha(0.8)
      .css();
  }, [pageColor]);

  // Combine scroll state with context visibility - navbar shows only when scrolled down AND context allows it
  const shouldShowNavBar = isScrolledDown && isVisibleFromContext;

  return (
    <StyledStickyContainer
      backgroundColor={backgroundColor}
      isVisible={shouldShowNavBar}
      paddingTop={pxToRem(offset)}
    >
      <StyledContentWrapper>
        <NavBar
          {...navBarProps}
          variant="inline"
          paddingTop={0}
          expandToFullWidth
        />
      </StyledContentWrapper>
    </StyledStickyContainer>
  );
};
